import { FC, useEffect } from 'react';

import { DATE_FORMAT, daysFromNow, parse } from '@eneco/common/date';
import RichText from '@eneco/components/RichText/RichText';
import { Layout, NextButton, StepHeader } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { useFormatter } from '@eneco/i18n';
import { InputDatepicker, NotificationBox, Stack, Text } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { NewDeliveryDateStepRendering } from '@sitecore/types/NewDeliveryDateStep';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FlowContextSme } from '../../context';
import {
  ENECO_SME_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_DEFAULT,
  ENECO_SME_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MAX,
  ENECO_SME_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN,
} from '../../helpers';

interface FormFields {
  deliveryDate: string;
  consentConsiderationPeriod?: boolean;
}

export const DeliveryDateStep: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContextSme>();
  const { send: sendToFlowMachine } = useFlowActorRef();

  const isMoving = useFlowSelector(state => state.context.isMoving);
  const consentConsiderationPeriod = useFlowSelector(state => state.context.consentConsiderationPeriod);
  const deliveryDate = useFlowSelector(state => state.context.deliveryDate);

  const { format, date } = useFormatter();

  const minimumDays = ENECO_SME_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MIN;
  const maximumDays = ENECO_SME_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_MAX;

  const minimumDate = daysFromNow(minimumDays);
  const maximumDate = daysFromNow(maximumDays);

  const defaultDate = date.short(daysFromNow(ENECO_SME_ENERGY_FLOW_CONTRACT_START_DATE_IN_DAYS_DEFAULT));

  const { NewDeliveryDateStep: { fields: textualData } = {} } = usePlaceholderContent<{
    NewDeliveryDateStep: NewDeliveryDateStepRendering;
  }>();

  const formSchema = yup.object({
    deliveryDate: yup
      .string()
      .required(textualData?.deliveryDate?.deliveryDateFormField?.value?.requiredMessage)
      .test({
        message: isMoving
          ? (textualData?.deliveryDate?.deliveryDateFormField?.value?.requiredMessage ?? '')
          : format(textualData?.deliveryDate?.deliveryDateFormField?.value?.validationMinValueMessage ?? '', {
              minimumDays,
            }),
        test: currentDate => !!currentDate && parse(currentDate, DATE_FORMAT) >= minimumDate,
      })
      .test({
        message: format(textualData?.deliveryDate?.deliveryDateFormField?.value?.validationMaxValueMessage ?? '', {
          maximumDate: date.short(maximumDate),
        }),
        test: currentDate => !!currentDate && parse(currentDate, DATE_FORMAT) <= maximumDate,
      }),
  });

  const {
    handleSubmit,
    getValues,
    setValue,
    control,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      deliveryDate: deliveryDate ?? defaultDate,
      consentConsiderationPeriod,
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formField = getValues();

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          ...formField,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <StepHeader textualData={textualData} />
      <Controller
        control={control}
        name="deliveryDate"
        render={({ field: { onChange, value, name } }) => (
          <InputDatepicker
            name={name}
            label={textualData?.deliveryDate?.deliveryDateFormField?.value?.label ?? ''}
            placeholder={textualData?.deliveryDate?.deliveryDateFormField?.value?.placeholder}
            hint={textualData?.deliveryDate?.deliveryDateFormField?.value?.hint}
            error={errors.deliveryDate?.message}
            value={value}
            onDaySelect={d => onChange(d && date.short(d as Date))}
            onDayClick={() => {
              setValue('consentConsiderationPeriod', false);
            }}
            fromDate={minimumDate}
            toDate={maximumDate}
            defaultMonth={parse(value, DATE_FORMAT)}
            isReadOnly
          />
        )}
      />
      {textualData?.notifications?.doesNotMoveOutNotification && (
        <Stack.Item>
          <Text size="BodyS">
            <NotificationBox
              isAlert={false}
              variant={textualData?.notifications?.doesNotMoveOutNotification?.value?.variant}
              text={<RichText html={textualData?.notifications?.doesNotMoveOutNotification?.value?.content} />}
            />
          </Text>
        </Stack.Item>
      )}

      <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
    </Layout.Content>
  );
};
